import { z } from "zod";

import { assetTypeSchema } from "./assets";

/**
 * The temperature record for one asset over one period.
 *
 * Named a *record* and not a compliance report, deliberately. The Argentine
 * Food Code asks refrigerated premises for "a max/min thermometer or a
 * temperature recording device", and being the recording device is something
 * we can evidence. Asserting that the premises therefore comply is not: it
 * depends on the inspector, on the rest of the establishment and, for
 * regulated activities, on a calibration we do not have. The master prompt
 * rules out presenting this system as certified or calibrated, so the
 * document states what was measured and leaves the conclusion to the reader
 * (ADR-073).
 *
 * Three things the record refuses to hide, because a temperature record that
 * hides them is worse than none — it is a document asserting something false:
 *
 * 1. **Coverage.** How many readings were expected against how many arrived,
 *    and every stretch with no readings at all. A record silently missing the
 *    six hours the sensor was offline reads as an unbroken cold chain.
 * 2. **The difference between an excursion and an incident.** An excursion is
 *    the raw data leaving the range; an incident is what the alert engine
 *    raised, which needs the condition sustained. Short excursions raise no
 *    incident, so showing only incidents understates and showing only
 *    excursions contradicts the incident list. Both are here.
 * 3. **The absent calibration.** No sensor has a calibration certificate and
 *    nothing in the product records one, so the record says so in as many
 *    words rather than letting a clean-looking document imply otherwise.
 */

/**
 * The longest period one record may cover.
 *
 * A month, because that is the natural unit of the thing being evidenced — a
 * record is produced for a period, and an inspection asks about one — and
 * because the response carries every reading so the client can build the CSV
 * without a second request. At the default interval a month is a few thousand
 * readings; a year would be a payload nobody can print and a query nobody
 * bounded. A longer history is twelve of these, not one bigger one.
 */
export const TEMPERATURE_RECORD_MAX_DAYS = 31;

/**
 * Assumed gap between uplinks when the device has no configuration row.
 *
 * Matches the column default in `DeviceConfiguration`. It is what the
 * expected-reading count is built from. Five minutes is what the pilot's
 * EM300-TH units are set to (ADR-094), not a datasheet figure, so the record
 * reports the assumption rather than burying it.
 */
export const ASSUMED_REPORTING_INTERVAL_SECONDS = 300;

export const temperatureRecordQuerySchema = z
  .object({
    from: z.iso.datetime(),
    to: z.iso.datetime(),
  })
  .refine((value) => new Date(value.from) < new Date(value.to), {
    message: "`from` must be earlier than `to`.",
    path: ["from"],
  })
  .refine(
    (value) =>
      new Date(value.to).getTime() - new Date(value.from).getTime() <=
      TEMPERATURE_RECORD_MAX_DAYS * 24 * 60 * 60 * 1000,
    {
      message: `A record may cover at most ${TEMPERATURE_RECORD_MAX_DAYS} days.`,
      path: ["to"],
    },
  );

export type TemperatureRecordQuery = z.infer<typeof temperatureRecordQuerySchema>;

/** Which end of the target range a reading fell outside. */
export const RANGE_BOUNDS = ["MIN", "MAX"] as const;
export const rangeBoundSchema = z.enum(RANGE_BOUNDS);
export type RangeBound = z.infer<typeof rangeBoundSchema>;

/**
 * A run of consecutive readings outside the asset's target range.
 *
 * `minutes` is measured from the first reading outside the range to the last
 * one, which **understates the excursion by up to one reporting interval** at
 * each end: the temperature crossed the bound somewhere between the last good
 * reading and the first bad one, and nothing observed when. Stated here
 * because the alternative — padding it by an interval — would be inventing
 * time nobody measured.
 */
export const temperatureExcursionSchema = z.object({
  startedAt: z.iso.datetime(),
  endedAt: z.iso.datetime(),
  minutes: z.number(),
  readings: z.number().int(),
  /** The furthest the temperature got from the range during the run. */
  extremeC: z.number(),
  bound: rangeBoundSchema,
});

export type TemperatureExcursion = z.infer<typeof temperatureExcursionSchema>;

/**
 * A stretch of the period with no readings from any device on the asset.
 *
 * Includes the edges: a sensor installed midway through the month leaves a
 * gap from the start of the period, and one that died before the end leaves a
 * gap to the end. Both are facts an inspector is entitled to see.
 */
export const recordGapSchema = z.object({
  startedAt: z.iso.datetime(),
  endedAt: z.iso.datetime(),
  minutes: z.number(),
});

export type RecordGap = z.infer<typeof recordGapSchema>;

/**
 * A device that contributed readings, identified so the record is
 * attributable.
 *
 * By DevEUI, manufacturer and model, because a device has no name in this
 * system — and for a record somebody may have to defend, the serial number
 * of the instrument is the right identity anyway.
 */
export const recordDeviceSchema = z.object({
  id: z.string(),
  devEui: z.string(),
  manufacturer: z.string(),
  model: z.string(),
  /** From the device's configuration, or the assumed default. */
  reportingIntervalSeconds: z.number().int(),
  /** Whether that interval was configured or assumed, since it drives coverage. */
  intervalAssumed: z.boolean(),
  /**
   * The part of the period this device was registered for.
   *
   * Registration is not installation — a sensor registered on the 3rd may
   * have been mounted on the 5th — but it is the closest date we hold, and
   * expecting readings from before a device existed would report the
   * shortfall as missing data. When these differ from the period, the asset
   * was only watched for part of it, which the document says out loud.
   */
  watchingFrom: z.iso.datetime(),
  watchingTo: z.iso.datetime(),
});

export type RecordDevice = z.infer<typeof recordDeviceSchema>;

/** Summary statistics for one measured quantity over the period. */
export const measurementSummarySchema = z.object({
  min: z.number(),
  max: z.number(),
  mean: z.number(),
  readings: z.number().int(),
});

export type MeasurementSummary = z.infer<typeof measurementSummarySchema>;

export const recordCoverageSchema = z.object({
  /**
   * Readings the devices should have sent over the period, from each one's
   * interval. Null when the asset has no device, where the honest answer is
   * that there was nothing to expect rather than zero.
   */
  expectedReadings: z.number().int().nullable(),
  receivedReadings: z.number().int(),
  /** Received over expected, capped at 100. Null when nothing was expected. */
  percent: z.number().nullable(),
  gaps: z.array(recordGapSchema),
  /**
   * How long a stretch without readings has to be before it is listed as a
   * gap. Published with the record so the document can state its own
   * definition rather than leaving the reader to assume one.
   */
  gapThresholdMinutes: z.number().int(),
});

export type RecordCoverage = z.infer<typeof recordCoverageSchema>;

/** One reading, as the CSV needs it. */
export const recordReadingSchema = z.object({
  recordedAt: z.iso.datetime(),
  deviceId: z.string(),
  temperatureC: z.number().nullable(),
  humidityPercent: z.number().nullable(),
  /** Null when the asset has no temperature range, or the reading has no temperature. */
  withinRange: z.boolean().nullable(),
});

export type RecordReading = z.infer<typeof recordReadingSchema>;

/** An incident the alert engine raised on this asset during the period. */
export const recordIncidentSchema = z.object({
  id: z.string(),
  type: z.string(),
  severity: z.string(),
  status: z.string(),
  startedAt: z.iso.datetime(),
  resolvedAt: z.iso.datetime().nullable(),
  acknowledgedAt: z.iso.datetime().nullable(),
  triggerValue: z.number().nullable(),
  thresholdValue: z.number().nullable(),
});

export type RecordIncident = z.infer<typeof recordIncidentSchema>;

export const temperatureRecordSchema = z.object({
  organizationName: z.string(),
  siteName: z.string(),
  assetId: z.string(),
  assetName: z.string(),
  assetType: assetTypeSchema,

  /** Echoes the window queried, in UTC. Storage is always UTC (ADR-021). */
  range: z.object({ from: z.iso.datetime(), to: z.iso.datetime() }),
  /**
   * The organization's timezone, which is the one the document is printed in.
   * Carried on the payload so the reader knows which clock the times are on.
   */
  timeZone: z.string(),

  targetTempMinC: z.number().nullable(),
  targetTempMaxC: z.number().nullable(),
  /**
   * Whether the readings were compared against anything at all.
   *
   * False when the asset has no temperature range, in which case the record
   * is a log and not an assessment: `excursions` is empty because nothing was
   * judged, which is a different statement from nothing having gone wrong.
   */
  judged: z.boolean(),

  devices: z.array(recordDeviceSchema),
  coverage: recordCoverageSchema,
  temperature: measurementSummarySchema.nullable(),
  humidity: measurementSummarySchema.nullable(),
  excursions: z.array(temperatureExcursionSchema),
  /** Sum of the excursion durations. Understates, for the reason above. */
  minutesOutOfRange: z.number(),
  incidents: z.array(recordIncidentSchema),
  readings: z.array(recordReadingSchema),

  generatedAt: z.iso.datetime(),
  /** Who asked for it. The request is also written to the audit trail. */
  generatedByEmail: z.string().nullable(),
});

export type TemperatureRecord = z.infer<typeof temperatureRecordSchema>;
